import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const Landing = () => {
  const { login, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Show error if redirected with error state
    if (location.state?.error) {
      toast.error(location.state.error);
      // Clear the error from state
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    // Redirect to dashboard if already authenticated
    if (!isLoading && isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-400"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Header - Centered Logo */}
      <header className="p-6">
        <div className="max-w-7xl mx-auto flex justify-center items-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_stocktracker-243/artifacts/ledhfika_250%20x%20100%20-%20TAP-AASMA%20Logo.png" 
            alt="AASMA TAP" 
            className="h-20"
          />
        </div>
      </header>

      {/* Hero Section */}
      <main className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Trade Journal for
            <span className="text-yellow-400"> Indian Markets</span>
          </h1>
          <p className="text-xl text-gray-400 mb-12 max-w-2xl mx-auto">
            Track your NSE & MCX futures trades, import positions from your Broker(s), 
            and analyze your trading performance with powerful insights.
          </p>

          <Button
            onClick={login}
            size="lg"
            className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-semibold px-8 py-6 text-lg rounded-xl shadow-lg shadow-yellow-400/20 transition-all hover:shadow-yellow-400/40"
          >
            <svg className="w-6 h-6 mr-3" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Sign in with Google
          </Button>

          <p className="text-sm text-gray-500 mt-4">
            Access restricted to authorized users only
          </p>
        </div>

        {/* Features */}
        <div className="grid md:grid-cols-3 gap-8 mt-24">
          <FeatureCard
            icon="📊"
            title="Trade Journaling"
            description="Log your trades with entry/exit prices, lot sizes, and track P&L automatically"
          />
          <FeatureCard
            icon="🔗"
            title="Broker Integration"
            description="Import positions directly from your Broker(s) and reconcile with your journal"
          />
          <FeatureCard
            icon="📈"
            title="Performance Analytics"
            description="Analyze win rate, equity curve, and identify patterns in your trading"
          />
        </div>

        {/* Supported Markets */}
        <div className="mt-24 text-center">
          <p className="text-gray-500 mb-6">Supported Markets</p>
          <div className="flex justify-center space-x-12">
            <div className="text-center">
              <div className="text-3xl font-bold text-white">NSE</div>
              <div className="text-sm text-gray-500">Index & Stock Futures</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white">MCX</div>
              <div className="text-sm text-gray-500">Commodity Futures</div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 text-center text-gray-600 text-sm">
        <p>© 2026 AASMA Ventures. All rights reserved.</p>
      </footer>
    </div>
  );
};

const FeatureCard = ({ icon, title, description }) => (
  <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6 text-center hover:border-yellow-400/50 transition-colors">
    <div className="text-4xl mb-4">{icon}</div>
    <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
    <p className="text-gray-400 text-sm">{description}</p>
  </div>
);

export default Landing;
